// Cross-app session handoff: auction.ai -> scraper.auction.ai.
//
// The two apps are separate origins and cannot share localStorage, so the
// session has to travel. It used to travel as a `?token=` query parameter,
// which had two problems. A live credential ended up in browser history, nginx
// access logs and Referer headers; and only the *access* token could be sent,
// leaving the scraper holding a bearer it had no way to renew. That token
// expired within the hour, the scraper bounced back here for another one, and
// for a while it bounced forever.
//
// Both hosts sit under auction.ai, so a cookie scoped to the parent domain
// reaches the scraper without ever appearing in a URL, and it can carry the
// refresh token as well. The scraper reads it once, deletes it, and hands it to
// Supabase, which owns renewal from then on. The cookie is short-lived and
// single-use: this file writes it, the scraper deletes it on read.

export const HANDOFF_COOKIE = 'aai_handoff';

// Long enough to survive the redirect and a slow page load, short enough that a
// cookie nobody consumed is gone before it matters.
const HANDOFF_TTL_SECONDS = 120;

// ".auction.ai" in production, so scraper.auction.ai receives it. On localhost
// the attribute must be omitted: cookies ignore ports, so a domain-less cookie
// is already shared between dev servers running on different ports.
const handoffCookieDomain = () => {
  // Explicit override for staging or local end-to-end runs, where the two apps
  // sit under some other shared parent. Must match the scraper's setting.
  const configured = (process.env.REACT_APP_HANDOFF_COOKIE_DOMAIN || '').trim();
  if (configured) return configured;
  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'auction.ai' || host.endsWith('.auction.ai')) return '.auction.ai';
  return null;
};

/**
 * Leave the current Supabase session where the scraper can pick it up.
 *
 * `session` must be the object from `supabase.auth.getSession()`, not the copy
 * mirrored into localStorage — only the real session carries a refresh token,
 * and a handoff without one is exactly the bug this replaces.
 *
 * Returns true when the cookie was written.
 */
export const writeSessionHandoff = (session, user) => {
  if (!session || !session.access_token) return false;

  const payload = JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token || null,
    user: user || null,
  });

  const parts = [
    `${HANDOFF_COOKIE}=${encodeURIComponent(payload)}`,
    'path=/',
    `max-age=${HANDOFF_TTL_SECONDS}`,
    'samesite=lax',
  ];
  const domain = handoffCookieDomain();
  if (domain) parts.push(`domain=${domain}`);
  // Omitted on plain http so local development still works; production is https.
  if (window.location.protocol === 'https:') parts.push('secure');

  try {
    document.cookie = parts.join('; ');
    return true;
  } catch (_e) {
    return false;
  }
};
